import { useAuthStore } from '@/stores/authStore';

/**
 * usePermissions — role-based boolean helpers consumed by UI guards.
 *
 * Backend remains the source of truth: a super-admin override of any UI
 * guard here won't actually let the caller hit a protected endpoint.
 * Keep that in mind when adding new permissions — don't rely on these
 * flags for security, only for UX (hiding buttons the user can't use).
 *
 * Super admins bypass every check by design.
 *
 * See REFACTORING_FRONTEND_PLAN.md §24.4 and §24.6.
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const active = useAuthStore((s) => s.activeMembership);

  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const isTenantAdmin = isSuperAdmin || active?.role === 'tenant_admin';
  const isCaller = isTenantAdmin || active?.role === 'caller';

  return {
    // Identity
    isSuperAdmin,
    isTenantAdmin,
    isCaller,

    // Admin surface (super admin only)
    canListAllOrganisations: isSuperAdmin,
    canCreateOrganisation: isSuperAdmin,
    canOverrideQuota: isSuperAdmin,

    // Tenant admin surface
    canProvisionNumber: isTenantAdmin,
    canReleaseNumber: isTenantAdmin,
    canManageMembers: isTenantAdmin,
    canChangePlan: isTenantAdmin,
    canAccessBillingPortal: isTenantAdmin,

    // Caller surface
    canMakeCalls: isCaller,
    canQualifyCalls: isCaller,
    canModifyOwnCoaching: isCaller,
  };
}
